import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { useUser } from '@renderer/hooks/useUser'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'

export interface SetupCallBooking {
  id: string
  start: string
  end: string
  timezone: string
  topics: Array<string>
  hostName: string
}

/** How the user has answered the invite, kept on their account so it follows them across devices. */
export interface SetupCallInvite {
  firstActionAt: string | null
  snoozedUntil: string | null
  dismissed: boolean
  peeked: 'first' | 'later' | null
}

export type SetupCallInviteChange = Partial<{
  firstActionAt: string
  snoozedUntil: string
  dismissed: true
  peeked: 'first' | 'later'
}>

export interface SetupCallState {
  booking: SetupCallBooking | null
  /** The signed-in user object's own `createdAt` is when this session began, not when the account was made. */
  signedUpAt: string | null
  invite: SetupCallInvite
}

export interface SetupCallAvailability {
  /** Open 30-minute slot starts, as UTC instants. */
  slots: Array<string>
  window: { start: string; end: string }
}

export const SETUP_CALL_TOPICS = [
  'Meeting notes',
  'Follow-ups',
  'Connecting my tools',
  'Using it with my team',
  'Something else',
] as const

const STATE_KEY = 'setup-call'

function useSetupCallFetch() {
  const { getAuthToken } = useAuth()
  return async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const response = await fetch(getChatApiRoute(['api/v1/setup-call', path].filter(Boolean).join('/')), {
      ...init,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getAuthToken()}` },
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(body.error ?? `Setup call request failed (${response.status})`)
    return body as T
  }
}

/** The user's upcoming setup call (`null` once loaded with none booked) and when they signed up. */
export function useSetupCallState(enabled: boolean) {
  const request = useSetupCallFetch()
  const userId = useUser()?.id
  return useQuery({
    queryKey: [STATE_KEY, userId],
    enabled: enabled && !!userId,
    queryFn: () => request<SetupCallState>(''),
    staleTime: 5 * 60 * 1000,
  })
}

export function useSetupCallAvailability(enabled: boolean) {
  const request = useSetupCallFetch()
  return useQuery({
    queryKey: ['setup-call-availability'],
    enabled,
    queryFn: () => request<SetupCallAvailability>('availability'),
    // The hosts' calendars change under the picker; a minute old is as stale as it should get.
    staleTime: 60 * 1000,
  })
}

function useStoreBooking() {
  const queryClient = useQueryClient()
  const userId = useUser()?.id
  return async (booking: SetupCallBooking) => {
    // A state fetch still in flight read the old booking, so it must not land over this one
    await queryClient.cancelQueries({ queryKey: [STATE_KEY, userId] })
    queryClient.setQueryData<SetupCallState>([STATE_KEY, userId], (state) => ({
      signedUpAt: state?.signedUpAt ?? null,
      invite: state?.invite ?? NO_INVITE_ANSWER,
      booking,
    }))
  }
}

const NO_INVITE_ANSWER: SetupCallInvite = { firstActionAt: null, snoozedUntil: null, dismissed: false, peeked: null }

/** Records an answer to the invite; it shows at once, and the server merges it with what other devices sent. */
export function useUpdateSetupCallInvite() {
  const request = useSetupCallFetch()
  const queryClient = useQueryClient()
  const userId = useUser()?.id
  return useMutation({
    mutationFn: async (change: SetupCallInviteChange) =>
      (await request<{ invite: SetupCallInvite }>('invite', { method: 'PUT', body: JSON.stringify(change) })).invite,
    onMutate: async (change) => {
      await queryClient.cancelQueries({ queryKey: [STATE_KEY, userId] })
      // Mirrors the server's merge: the earliest first action wins, and Don't show again can't be undone
      queryClient.setQueryData<SetupCallState>(
        [STATE_KEY, userId],
        (state) =>
          state && {
            ...state,
            invite: {
              ...state.invite,
              ...change,
              firstActionAt: [state.invite.firstActionAt, change.firstActionAt].filter((at) => at).sort()[0] ?? null,
              dismissed: state.invite.dismissed || !!change.dismissed,
            },
          },
      )
    },
    // The server's merge includes what other devices sent
    onSettled: () => queryClient.invalidateQueries({ queryKey: [STATE_KEY, userId] }),
  })
}

/** Books the slot, or moves the user's booking to it. */
export function useBookSetupCall() {
  const request = useSetupCallFetch()
  const storeBooking = useStoreBooking()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { start: string; timezone: string }) =>
      (await request<{ booking: SetupCallBooking }>('booking', { method: 'PUT', body: JSON.stringify(input) })).booking,
    onSuccess: storeBooking,
    // Taken or not, the slots the picker showed are out of date.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['setup-call-availability'] }),
  })
}

export function useSendSetupCallTopics() {
  const request = useSetupCallFetch()
  const storeBooking = useStoreBooking()
  return useMutation({
    mutationFn: async (topics: Array<string>) =>
      (
        await request<{ booking: SetupCallBooking }>('booking/topics', {
          method: 'PUT',
          body: JSON.stringify({ topics }),
        })
      ).booking,
    onSuccess: storeBooking,
  })
}
